import {
  APP_ADD_LIST_SETTING_ITEM,
  APP_REMOVE_LIST_SETTING_ITEM,
  APP_UPDATE_ADMIN_INITIALIZED,
  APP_UPDATE_DATE_RANGE,
  APP_UPDATE_INITIALIZED,
  APP_UPDATE_PAGE_TITLE,
  APP_UPDATE_PICKER_STATUS,
  APP_UPDATE_SETTING,
  APP_UPDATE_SETTINGS,
  APP_UPDATE_STRIPE_KEY,
  APP_ADD_TRANSLATION
} from '@/store/mutypes';
import {
  AppState,
  DateRange,
  RootState,
  Setting,
  UpdateListSettingItemDTO
} from '@/types';
import { ActionContext } from 'vuex';
import { DateTime } from 'luxon';
import { appSvc, settingSvc } from '@/services';
import { Message } from 'element-ui';
import Vue from 'vue';

// initial state
const modState: AppState = {
  appInitialized: false,
  adminInitialized: false,
  pageTitle: 'UpService',
  showDateRangePicker: false,
  settings: [],
  dateRange: [
    DateTime.local().minus(30 * 24 * 60 * 60 * 1000),
    DateTime.local()
  ],
  stripeKey: '',
  translations: {}
};

// getters
const getters = {
  getSetting: (state: AppState) => (key: string, group = 'general') => {
    const setting: Setting = state.settings.find(
      (s: Setting) => s.key === key && s.group === group
    ) as Setting;
    return setting ? setting.value : null;
  },
  getSettingsByGroup: (state: AppState) => (group = 'general') => {
    return state.settings.filter((s: Setting) => s.group === group);
  }
};

// actions, to handle async call
const actions = {
  /**
   * initSettings action
   * @param param0
   */
  async initSettings({ commit }: ActionContext<AppState, RootState>) {
    const settings = await settingSvc.getSettings();
    commit(APP_UPDATE_SETTINGS, settings);
  },
  /**
   * initAppState action
   * @param param0
   */
  async initAppState({
    dispatch,
    state,
    commit
  }: ActionContext<AppState, RootState>) {
    if (!state.appInitialized) {
      try {
        await Promise.all([dispatch('initServices'), dispatch('getStripeKey')]);
        commit(APP_UPDATE_INITIALIZED, true);
      } catch (e) {
        Message.error(
          'Failed to initial the app, please try reload this page.'
        );
      } finally {
        //
      }
    }
  },
  async initAdminState({
    dispatch,
    state,
    commit
  }: ActionContext<AppState, RootState>) {
    if (!state.adminInitialized) {
      await Promise.all([
        dispatch('initAppState'),
        dispatch('initSettings'),
        dispatch('initRoles')
      ]);
      commit(APP_UPDATE_ADMIN_INITIALIZED, true);
    }
  },
  async updateSetting(
    { commit }: ActionContext<AppState, RootState>,
    setting: Setting
  ) {
    await settingSvc.mutateSetting(setting);
    commit(APP_UPDATE_SETTING, setting);
  },
  async addListSettingItem(
    { commit }: ActionContext<AppState, RootState>,
    dto: UpdateListSettingItemDTO
  ) {
    await settingSvc.addListSettingItem(dto);
    commit(APP_ADD_LIST_SETTING_ITEM, dto);
  },
  async removeListSettingItem(
    { commit }: ActionContext<AppState, RootState>,
    dto: UpdateListSettingItemDTO
  ) {
    await settingSvc.removeListSettingItem(dto);
    commit(APP_REMOVE_LIST_SETTING_ITEM, dto);
  },
  /**
   * getStripeKey action
   * @param param0
   */
  async getStripeKey({ commit }: ActionContext<AppState, RootState>) {
    const key = await appSvc.getStripeKey();
    commit(APP_UPDATE_STRIPE_KEY, key);
  }
};

const mutations = {
  [APP_ADD_TRANSLATION](
    state: AppState,
    { key, value }: { key: string; value: string }
  ) {
    Vue.set(state.translations, key, value);
  },
  [APP_UPDATE_PICKER_STATUS](state: AppState, status: boolean) {
    state.showDateRangePicker = status;
  },
  [APP_UPDATE_INITIALIZED](state: AppState, status: boolean) {
    state.appInitialized = status;
  },
  [APP_UPDATE_ADMIN_INITIALIZED](state: AppState, status: boolean) {
    state.adminInitialized = status;
  },
  [APP_UPDATE_STRIPE_KEY](state: AppState, key: string) {
    state.stripeKey = key;
  },
  [APP_UPDATE_PAGE_TITLE](state: AppState, title: string) {
    state.pageTitle = title;
  },
  [APP_UPDATE_DATE_RANGE](state: AppState, range: DateRange) {
    state.dateRange = range;
  },
  [APP_UPDATE_SETTINGS](state: AppState, settings: Setting[]) {
    state.settings.push(...settings);
  },
  [APP_UPDATE_SETTING](state: AppState, setting: Setting) {
    state.settings.forEach((s: Setting) => {
      if (s.key === setting.key) {
        s.value = setting.value;
      }
    });
  },
  [APP_ADD_LIST_SETTING_ITEM](state: AppState, dto: UpdateListSettingItemDTO) {
    state.settings.forEach((s: Setting) => {
      if (s.id === dto.id) {
        s.value.push(dto.item);
      }
    });
  },
  [APP_REMOVE_LIST_SETTING_ITEM](
    state: AppState,
    dto: UpdateListSettingItemDTO
  ) {
    state.settings.forEach((s: Setting) => {
      if (s.id === dto.id) {
        s.value.splice(s.value.indexOf(dto.item), 1);
      }
    });
  }
};

export default {
  state: modState,
  getters,
  actions,
  mutations
};
